/**
 * 点选（ADR-0004「拾取用 CPU 网格空间索引」）：在屏幕上离点击处最近的一颗星。
 *
 * 判定和以前逐颗投影的做法完全一样（pickLinear 留着做对照，测试里逐个比对）：
 * 星在屏幕上离点击处 16 像素以内（大星按它自己的半径），按「距离² + 深度 × 0.02」取最小的。
 * 不同的是先用空间网格粗筛：只沿着点击的那条射线，查射线附近格子里的星。
 *
 * 为什么不会漏：星 P 在深度 d 处离点击处 r 像素以内，等价于它离「射线在同一深度的那一点」不超过 r·d / pxScale，
 * 所以它离射线也不超过这么远；d 不超过镜头到所有星包围盒最远角的距离 D，于是 ρ = max(16·D / pxScale, 最大星的半径)
 * 是一个上限。沿射线每隔 step 取一个点、查半径 ρ + step / 2 的立方体，就盖住了所有可能被点中的星。
 */
import * as THREE from 'three';
import type { StarGrid } from './grid.ts';

export interface PickInput {
  camera: THREE.Camera;
  /** 点击处相对画布左上角的坐标和画布大小，CSS 像素 */
  x: number;
  y: number;
  width: number;
  height: number;
  positions: Float32Array;
  sizes: Float32Array;
  borns: Float32Array;
  count: number;
  /** 还没出现（born 在这之后）的星点不中 */
  time: number;
  grid: StarGrid;
  /** 所有星里最大的 size，决定大星的点中半径 */
  maxSize: number;
}

/** 屏幕上的最小点中半径（像素） */
const MIN_PX = 16;
/** 沿射线取样的间隔（世界单位），和网格一格一样大 */
const STEP = 6;

const vp = new THREE.Matrix4();
const ray = new THREE.Vector3();
const at = new THREE.Vector3();
const cands: number[] = [];
/** 去重用：每次点选换一个记号，不用每次清空 */
let seen = new Int32Array(0);
let stamp = 0;

/** 判定一颗星，打分；不中返回 Infinity */
function scorer(o: PickInput): (i: number) => number {
  o.camera.updateMatrixWorld();
  const e = vp.multiplyMatrices(o.camera.projectionMatrix, o.camera.matrixWorldInverse).elements;
  const { x, y, width: w, height: h, positions: pos, sizes: size, borns: born, time } = o;
  const pxScale = h * 0.5 * o.camera.projectionMatrix.elements[5]!;
  return (i) => {
    if (born[i]! > time) return Infinity;
    const px = pos[i * 3]!, py = pos[i * 3 + 1]!, pz = pos[i * 3 + 2]!;
    const cw = e[3]! * px + e[7]! * py + e[11]! * pz + e[15]!;
    if (cw <= 0.05) return Infinity;
    const sx = ((e[0]! * px + e[4]! * py + e[8]! * pz + e[12]!) / cw) * 0.5 * w + 0.5 * w;
    const sy = (-(e[1]! * px + e[5]! * py + e[9]! * pz + e[13]!) / cw) * 0.5 * h + 0.5 * h;
    const dx = sx - x, dy = sy - y, d2 = dx * dx + dy * dy;
    const r = Math.max(MIN_PX, (size[i]! * pxScale) / cw / 2);
    return d2 > r * r ? Infinity : d2 + cw * 0.02;
  };
}

/** 对照用：逐颗判定所有星 */
export function pickLinear(o: PickInput): number {
  const score = scorer(o);
  let best = -1, bestScore = Infinity;
  for (let i = 0; i < o.count; i++) {
    const s = score(i);
    if (s < bestScore) {
      bestScore = s;
      best = i;
    }
  }
  return best;
}

/** 射线 origin + t·dir 在包围盒（每边外扩 pad）里的那一段 [t0, t1]；不相交返回 null */
function slab(origin: THREE.Vector3, dir: THREE.Vector3, b: Float64Array, pad: number): [number, number] | null {
  let t0 = 0, t1 = Infinity;
  const o = [origin.x, origin.y, origin.z], d = [dir.x, dir.y, dir.z];
  for (let k = 0; k < 3; k++) {
    const lo = b[k]! - pad, hi = b[k + 3]! + pad;
    if (Math.abs(d[k]!) < 1e-12) {
      if (o[k]! < lo || o[k]! > hi) return null;
      continue;
    }
    let a = (lo - o[k]!) / d[k]!, c = (hi - o[k]!) / d[k]!;
    if (a > c) [a, c] = [c, a];
    t0 = Math.max(t0, a);
    t1 = Math.min(t1, c);
    if (t0 > t1) return null;
  }
  return [t0, t1];
}

export function pickStar(o: PickInput): number {
  const b = o.grid.bounds;
  if (o.count === 0 || b[0]! > b[3]!) return -1;
  const cam = o.camera;
  cam.updateMatrixWorld();
  const origin = at.setFromMatrixPosition(cam.matrixWorld).clone();
  // 点击处对应的射线方向（投影矩阵里带着画面上移的偏移，和判定用的是同一个矩阵）
  ray.set((o.x / o.width) * 2 - 1, -(o.y / o.height) * 2 + 1, 0.5).unproject(cam).sub(origin).normalize();
  // D：镜头到包围盒最远角的距离，星的深度不会超过它
  let D = 0;
  for (let k = 0; k < 8; k++) {
    const cx = b[k & 1 ? 3 : 0]!, cy = b[k & 2 ? 4 : 1]!, cz = b[k & 4 ? 5 : 2]!;
    D = Math.max(D, Math.hypot(cx - origin.x, cy - origin.y, cz - origin.z));
  }
  const pxScale = o.height * 0.5 * cam.projectionMatrix.elements[5]!;
  const rho = Math.max((MIN_PX * D) / pxScale, o.maxSize / 2) * 1.001 + 1e-3;
  const seg = slab(origin, ray, b, rho);
  if (!seg) return -1;
  if (seen.length < o.count) seen = new Int32Array(Math.max(o.count, seen.length * 2));
  if (++stamp >= 0x7fffffff) {
    seen.fill(0);
    stamp = 1;
  }
  cands.length = 0;
  const [t0, t1] = seg, n = Math.max(0, Math.ceil((t1 - t0) / STEP)), R = rho + STEP / 2;
  for (let k = 0; k <= n; k++) {
    const t = Math.min(t1, t0 + k * STEP);
    o.grid.collect(origin.x + ray.x * t, origin.y + ray.y * t, origin.z + ray.z * t, R, cands);
  }
  const score = scorer(o);
  let best = -1, bestScore = Infinity;
  for (const i of cands) {
    if (i >= o.count || seen[i] === stamp) continue;
    seen[i] = stamp;
    const s = score(i);
    // 分数一样时取序号小的，和逐颗判定的结果一致
    if (s < bestScore || (s === bestScore && s < Infinity && i < best)) {
      bestScore = s;
      best = i;
    }
  }
  return best;
}
